/**
 * Where each workspace lives, remembered on this device, and the claim the
 * token last carried. No Firebase import, so the root redirect can use it
 * without loading Firestore.
 *
 * Slugs never change once createOrg has written them, so `slug → orgId` is
 * safe to keep. Every entry is checked against the token's claim before it
 * is used, and against the org document's own slug once that arrives (see
 * org.tsx), so a stale or foreign entry costs one extra read, never the
 * wrong workspace. Browser storage can be missing or throw (private mode,
 * cleared site data); then every visit resolves the slug, as it always did.
 */
const SLUG_KEY = 'claripick.orgBySlug';
const LAST_KEY = 'claripick.lastWorkspace';
const claimKey = (uid: string) => `claripick.claim.${uid}`;

export function readSlugCache(): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(SLUG_KEY) ?? '{}') as unknown;
    return v && typeof v === 'object' ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/** Remember that `slug` is `orgId`; with `last`, that it was the one just opened. */
export function rememberWorkspace(slug: string, orgId: string, last = false): void {
  try {
    const cache = readSlugCache();
    if (cache[slug] !== orgId) localStorage.setItem(SLUG_KEY, JSON.stringify({ ...cache, [slug]: orgId }));
    if (last) localStorage.setItem(LAST_KEY, JSON.stringify({ slug, orgId }));
  } catch {
    /* nothing kept */
  }
}

export function forgetWorkspace(slug: string): void {
  try {
    const cache = readSlugCache();
    if (!(slug in cache)) return;
    delete cache[slug];
    localStorage.setItem(SLUG_KEY, JSON.stringify(cache));
  } catch {
    /* nothing kept, nothing to forget */
  }
}

/** The workspace this device last opened, if any. */
export function lastWorkspace(): { slug: string; orgId: string } | null {
  try {
    const v = JSON.parse(localStorage.getItem(LAST_KEY) ?? 'null') as { slug?: unknown; orgId?: unknown } | null;
    return v && typeof v.slug === 'string' && typeof v.orgId === 'string' ? { slug: v.slug, orgId: v.orgId } : null;
  } catch {
    return null;
  }
}

export function rememberClaim(uid: string, claim: Record<string, string>): void {
  try {
    localStorage.setItem(claimKey(uid), JSON.stringify(claim));
  } catch {
    /* nothing kept */
  }
}

/** The `o` claim this device last decoded for `uid`, or an empty one. */
export function rememberedClaim(uid: string): Record<string, string> {
  try {
    const v = JSON.parse(localStorage.getItem(claimKey(uid)) ?? '{}') as unknown;
    return v && typeof v === 'object' ? (v as Record<string, string>) : {};
  } catch {
    return {};
  }
}

/**
 * The `o` claim, { orgId: roleCode }, from the user's ID token. Offline with
 * a token past its hour, Auth can neither refresh nor decode it; then the
 * claim this device last saw stands in, since that is what the cached
 * workspace was opened with, and the server checks the real one when the
 * connection is back.
 */
export async function readOrgClaim(user: { uid: string; getIdTokenResult: () => Promise<{ claims: Record<string, unknown> }> }): Promise<Record<string, string>> {
  try {
    const result = await user.getIdTokenResult();
    const claim = { ...((result.claims.o ?? {}) as Record<string, string>) };
    rememberClaim(user.uid, claim);
    return claim;
  } catch {
    return rememberedClaim(user.uid);
  }
}
